/**
 * StockEntryWarehouseWidget — Contextual warehouse selector + date/remarks.
 *
 * Shows source/target warehouse fields based on the selected Stock Entry Type's
 * warehouseMode from STOCK_ACTIONS config. Also shows posting date and remarks.
 *
 * Hidden until stock_entry_type is selected (progressive disclosure).
 * Follows TemplateInfoWidget pattern (Plan 126).
 */

import { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import {
  STOCK_ACTIONS,
  type WarehouseMode,
} from "@/features/common/doc-dashboard/stockActionsConfig";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";

const i18n: Record<string, { en: string; th: string }> = {
  source_warehouse: { en: "Source Warehouse", th: "คลังต้นทาง" },
  target_warehouse: { en: "Target Warehouse", th: "คลังปลายทาง" },
  posting_date: { en: "Date", th: "วันที่" },
  remarks: { en: "Remarks (optional)", th: "หมายเหตุ (ไม่บังคับ)" },
  remarks_placeholder: { en: "Add a note about this entry...", th: "เพิ่มหมายเหตุเกี่ยวกับรายการนี้..." },
  select_warehouse: { en: "Select warehouse...", th: "เลือกคลังสินค้า..." },
};

function getWarehouseMode(stockEntryType: string): WarehouseMode | null {
  const action = STOCK_ACTIONS.find((a) => a.stockEntryType === stockEntryType);
  return action?.warehouseMode ?? null;
}

export function StockEntryWarehouseWidget({
  formData,
  onFieldChange,
  disabled,
  isSaving,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const stockEntryType = (formData.stock_entry_type as string) || "";
  const warehouseMode = useMemo(
    () => getWarehouseMode(stockEntryType),
    [stockEntryType]
  );

  // Don't render until type is selected
  if (!stockEntryType || !warehouseMode) {
    return null;
  }

  const showFrom = warehouseMode === "from" || warehouseMode === "from_to";
  const showArrow = warehouseMode === "from_to";

  return (
    <div>
      <SectionHeader
        label={getSectionTitleTranslation("Warehouse & Details", language)}
      />
      <div className="rounded-2xl overflow-hidden bg-card shadow-apple p-6 space-y-5">
        {/* Warehouse selectors */}
        <div className="space-y-4">
          {showArrow ? (
            /* Transfer mode: side-by-side with arrow */
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1.5">
                <Label className="text-sm font-medium">{t("source_warehouse")}</Label>
                <DocTypeSearchAutocomplete
                  doctype="Warehouse"
                  value={(formData.from_warehouse as string) || ""}
                  onChange={(val) => onFieldChange("from_warehouse", val)}
                  placeholder={t("select_warehouse")}
                  disabled={disabled || isSaving}
                  filters={{ is_group: 0 }}
                  showInitialSuggestions
                  minSearchLength={0}
                />
              </div>
              <div className="flex items-center justify-center h-10 w-10 shrink-0 text-muted-foreground">
                <ArrowRight className="h-5 w-5" />
              </div>
              <div className="flex-1 space-y-1.5">
                <Label className="text-sm font-medium">{t("target_warehouse")}</Label>
                <DocTypeSearchAutocomplete
                  doctype="Warehouse"
                  value={(formData.to_warehouse as string) || ""}
                  onChange={(val) => onFieldChange("to_warehouse", val)}
                  placeholder={t("select_warehouse")}
                  disabled={disabled || isSaving}
                  filters={{ is_group: 0 }}
                  showInitialSuggestions
                  minSearchLength={0}
                />
              </div>
            </div>
          ) : (
            /* Single warehouse mode */
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                {showFrom ? t("source_warehouse") : t("target_warehouse")}
              </Label>
              <DocTypeSearchAutocomplete
                doctype="Warehouse"
                value={
                  showFrom
                    ? (formData.from_warehouse as string) || ""
                    : (formData.to_warehouse as string) || ""
                }
                onChange={(val) =>
                  onFieldChange(showFrom ? "from_warehouse" : "to_warehouse", val)
                }
                placeholder={t("select_warehouse")}
                disabled={disabled || isSaving}
                filters={{ is_group: 0 }}
                showInitialSuggestions
                minSearchLength={0}
              />
            </div>
          )}
        </div>

        {/* Posting date */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">{t("posting_date")}</Label>
          <Input
            type="date"
            value={(formData.posting_date as string) || ""}
            onChange={(e) => onFieldChange("posting_date", e.target.value)}
            disabled={disabled || isSaving}
          />
        </div>

        {/* Remarks */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">{t("remarks")}</Label>
          <Textarea
            value={(formData.remarks as string) || ""}
            onChange={(e) => onFieldChange("remarks", e.target.value)}
            placeholder={t("remarks_placeholder")}
            rows={2}
            disabled={disabled || isSaving}
          />
        </div>
      </div>
    </div>
  );
}
